"use client"

import type { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useEffect, useRef, useState } from "react"
import { PRODUCT_COPY } from "../../lib/product-copy"
import { formatProductMoney } from "../../lib/product-money"
import { shortProductName } from "../../lib/product-title"
import { isConfiguredSinglePurchaseOption } from "../../lib/bundle-option"
import type {
  PurchaseSelection,
  StoreBundle,
  StoreBundleCatalogProduct,
  StoreBundleComposition,
  StoreBundleOption,
} from "../../types"
import styles from "./bundle-selector.module.css"

type Props = {
  product: HttpTypes.StoreProduct
  bundles: StoreBundle[]
  selection: PurchaseSelection
  onSelectionChange: (selection: PurchaseSelection) => void
}

type MenuState = { key: string; trigger: HTMLElement } | null

function SelectorChevron({
  className,
  testId,
  attention,
}: {
  className: string
  testId?: string
  attention?: "active" | "stopped"
}) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={className}
      data-testid={testId}
      data-attention={attention}
    >
      <path
        d="M5 7.5 10 12.5 15 7.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const expandComposition = (composition: StoreBundleComposition[], quantity: number) => {
  const slots = composition.flatMap((item) =>
    Array.from({ length: Math.max(0, item.quantity) }, () => ({
      product_id: item.product_id,
      variant_id: item.variant_id,
      quantity: 1,
    }))
  )
  while (slots.length < quantity) slots.push({ product_id: "", variant_id: "", quantity: 1 })
  return slots.slice(0, quantity)
}

const groupSlots = (slots: StoreBundleComposition[]) => {
  const grouped = new Map<string, StoreBundleComposition>()
  for (const slot of slots) {
    const key = `${slot.product_id}:${slot.variant_id}`
    const current = grouped.get(key)
    if (current) current.quantity += 1
    else grouped.set(key, { ...slot, quantity: 1 })
  }
  return [...grouped.values()]
}

const optionSelection = (bundle: StoreBundle, option: StoreBundleOption): Extract<PurchaseSelection, { kind: "bundle" }> => ({
  kind: "bundle",
  bundle_id: bundle.id,
  option_id: option.id,
  composition: option.default_composition.map((item) => ({ ...item })),
})

const getCatalog = (option: StoreBundleOption, product: HttpTypes.StoreProduct): StoreBundleCatalogProduct[] => {
  if (option.eligible_catalog?.length) return option.eligible_catalog
  const currentProductVariants = (product.variants ?? [])
    .filter((variant) => (option.eligible_variants ?? []).includes(variant.id))
    .map((variant) => ({
      variant_id: variant.id,
      title: variant.title ?? "Varyant",
      available: !variant.manage_inventory || Boolean(variant.allow_backorder) || Number(variant.inventory_quantity ?? 0) > 0,
    }))
  return (option.eligible_products ?? []).map((productId, index) => ({
    product_id: productId,
    handle: productId === product.id ? product.handle ?? "" : "",
    title: productId === product.id ? product.title ?? "Ürün" : `Paket ürünü ${index + 1}`,
    variants: productId === product.id ? currentProductVariants : [],
  }))
}

const companionIcon = (title: string) => {
  const normalized = title.toLowerCase()
  if (normalized.includes("earwipes")) return "👂"
  if (normalized.includes("freshwipes")) return "🦷"
  if (normalized.includes("eyewipes")) return "👁️"
  return ""
}

const bundleProductLabel = (title: string) => {
  const icon = companionIcon(title)
  return (
    <span className={styles.bundleProductLabel}>
      {icon ? <span className={styles.bundleProductIcon} aria-hidden="true">{icon}</span> : null}
      <span className={styles.bundleProductText}>{title}</span>
    </span>
  )
}

export default function BundleSelector({ product, bundles, selection, onSelectionChange }: Props) {
  const [menu, setMenu] = useState<MenuState>(null)
  const [hasOpenedSelector, setHasOpenedSelector] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const radioRefs = useRef(new Map<string, HTMLInputElement>())
  const hydrationRecoveredRef = useRef(false)
  const currencyCode = product.variants?.[0]?.calculated_price?.currency_code ?? "try"
  const singleVariant = selection.kind === "single"
    ? product.variants?.find((variant) => variant.id === selection.variant_id) ?? product.variants?.[0]
    : product.variants?.[0]

  useEffect(() => {
    if (!menu) return
    const close = (returnFocus: boolean) => {
      const trigger = menu.trigger
      setMenu(null)
      if (returnFocus) queueMicrotask(() => trigger.focus())
    }
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (menu.trigger.contains(target) || menuRef.current?.contains(target)) return
      close(true)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        close(true)
      }
    }
    document.addEventListener("mousedown", onMouseDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onMouseDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [menu])

  const selectOption = (bundle: StoreBundle, option: StoreBundleOption) => {
    if (!bundle.available || !option.available) return
    setMenu(null)
    if (isConfiguredSinglePurchaseOption(option)) {
      const variantId = singleVariant?.id ?? product.variants?.[0]?.id ?? ""
      if (!variantId) return
      onSelectionChange({ kind: "single", variant_id: variantId })
      return
    }
    if (!option.pricing) return
    onSelectionChange(optionSelection(bundle, option))
  }

  useEffect(() => {
    if (hydrationRecoveredRef.current || !bundles.length) return
    hydrationRecoveredRef.current = true

    for (const bundle of bundles) {
      for (const option of bundle.options) {
        const radio = radioRefs.current.get(`${bundle.id}:${option.id}`)
        if (!radio?.checked) continue
        const selected = isConfiguredSinglePurchaseOption(option)
          ? selection.kind === "single"
          : selection.kind === "bundle" && selection.bundle_id === bundle.id && selection.option_id === option.id
        if (!selected) selectOption(bundle, option)
        return
      }
    }
  }, [bundles, selection])

  if (!bundles.length) return null

  const selectorTitle = bundles[0]?.selector_title || PRODUCT_COPY.bundleHeading

  const changeSlot = (
    bundle: StoreBundle,
    option: StoreBundleOption,
    slotIndex: number,
    patch: Partial<StoreBundleComposition>
  ) => {
    const activeComposition = selection.kind === "bundle" && selection.bundle_id === bundle.id && selection.option_id === option.id
      ? selection.composition
      : option.default_composition
    const slots = expandComposition(activeComposition, option.quantity)
    slots[slotIndex] = { ...slots[slotIndex], ...patch, quantity: 1 }
    onSelectionChange({ kind: "bundle", bundle_id: bundle.id, option_id: option.id, composition: groupSlots(slots) })
  }

  const toggleMenu = (key: string, trigger: HTMLElement) => {
    const opening = menu?.key !== key
    if (opening) setHasOpenedSelector(true)
    setMenu(opening ? { key, trigger } : null)
  }

  const selectedOption = selection.kind === "bundle"
    ? bundles
        .find((bundle) => bundle.id === selection.bundle_id)
        ?.options.find((option) => option.id === selection.option_id) ?? null
    : null
  const companionProducts = selectedOption
    ? getCatalog(selectedOption, product)
        .filter((candidate) => candidate.product_id !== product.id && candidate.handle)
        .slice(0, 2)
    : []

  return (
    <section className={styles.bundleContainer} aria-labelledby="bundle-selector-title">
      <div className={styles.bundleHeader}>
        <div className={styles.bundleDividerLine} />
        <h3 id="bundle-selector-title" className={styles.bundleTitle}>{selectorTitle}</h3>
        <div className={styles.bundleDividerLine} />
      </div>

      <div className={styles.bundleOptions} role="radiogroup" aria-label="Satın alma seçimi">
        {bundles.flatMap((bundle) => bundle.options.map((option) => {
          const singleOption = isConfiguredSinglePurchaseOption(option)
          const isSelected = singleOption
            ? selection.kind === "single"
            : selection.kind === "bundle" && selection.bundle_id === bundle.id && selection.option_id === option.id
          const singleCurrent = Number(singleVariant?.calculated_price?.calculated_amount ?? 0)
          const singleCompare = singleVariant?.calculated_price?.original_amount == null
            ? null
            : Number(singleVariant.calculated_price.original_amount)
          const pricing = singleOption
            ? singleCurrent > 0
              ? {
                  compare_at_total: singleCompare ?? singleCurrent,
                  current_total: singleCurrent,
                  bundle_discount_total: 0,
                  expected_effective_total: singleCurrent,
                  unit_price: singleCurrent,
                  savings_amount: Math.max(0, (singleCompare ?? singleCurrent) - singleCurrent),
                  savings_percentage: 0,
                }
              : null
            : option.pricing
          const disabled = !bundle.available || !option.available || !pricing
          const catalog = getCatalog(option, product)
          const slots = expandComposition(
            !singleOption && selection.kind === "bundle" && selection.bundle_id === bundle.id && selection.option_id === option.id
              ? selection.composition
              : option.default_composition,
            option.quantity
          )
          const hasSelectors = !singleOption && option.composition_mode === "customer_selectable" && option.quantity > 0
          const popularText = option.show_highlight === false ? null : option.highlight_text || null
          const badgeText = option.show_badge === false ? null : option.badge_text || null
          const defaultTagText =
            option.is_default && option.show_default_tag && option.default_tag_text
              ? option.default_tag_text
              : null
          const unitLabel = bundle.unit_label || "Kutu"
          const slotLabel = bundle.slot_label || "Kutu"
          const selectorNote = bundle.selector_note ?? "Diğer VetPets ürünleriyle kombinleyebilirsin."
          const showTotalPrice = option.show_total_price !== false
          const showCompareAtPrice = option.show_compare_at_price !== false
          const showUnitPrice = option.show_unit_price !== false
          const showSavings = option.show_savings === true
          const purchaseChoiceId = `purchase-choice-${bundle.id}-${option.id}`

          return (
            <div
              key={`${bundle.id}:${option.id}`}
              className={`${styles.bundleOption} ${isSelected ? styles.bundleOptionSelected : ""} ${option.is_default ? styles.bundleOptionDefault : ""} ${disabled ? styles.bundleOptionDisabled : ""}`}

            >
              {!isSelected && !disabled ? (
                <label
                  htmlFor={purchaseChoiceId}
                  data-testid={`bundle-hit-area-${option.id}`}
                  className={styles.bundleOptionHitArea}
                  aria-hidden="true"
                />
              ) : null}
              <div className={styles.bundleOptionHeader}>
                <div className={styles.bundleOptionLeft}>
                  <input
                    id={purchaseChoiceId}
                    ref={(node) => {
                      const key = `${bundle.id}:${option.id}`
                      if (node) radioRefs.current.set(key, node)
                      else radioRefs.current.delete(key)
                    }}
                    className={styles.bundleRadio}
                    type="radio"
                    name="purchase-choice"
                    aria-label={option.title}
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => selectOption(bundle, option)}
                  />
                  <div className={styles.bundleInfo}>
                    <div className={styles.bundleNameRow}>
                      <span className={styles.bundleName}>{option.title}</span>
                      {badgeText ? <span className={styles.bundleTag}>{badgeText}</span> : null}
                      {defaultTagText ? <span className={styles.bundleTag}>{defaultTagText}</span> : null}
                    </div>
                    {option.subtitle?.trim() && option.subtitle.trim() !== `${option.quantity} ${unitLabel}` ? (
                      <div className={styles.bundleQuantity}>{option.subtitle}</div>
                    ) : null}
                    {pricing ? (
                      <>
                        <div className={styles.bundleQuantity}>{option.quantity} {unitLabel}</div>
                        {showUnitPrice ? (
                          <div className={styles.bundlePricePerUnit}>{formatProductMoney(pricing.unit_price, currencyCode)}{PRODUCT_COPY.bundlePerUnit}</div>
                        ) : null}
                        {showSavings && pricing.savings_amount > 0 ? (
                          <div className={styles.bundlePricePerUnit}>{formatProductMoney(pricing.savings_amount, currencyCode)} tasarruf</div>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
                <div className={styles.bundleOptionRight}>
                  {pricing ? (
                    <>
                      {showTotalPrice ? (
                        <strong className={styles.bundleFinalPrice}>{formatProductMoney(pricing.expected_effective_total, currencyCode)}</strong>
                      ) : null}
                      {showCompareAtPrice ? (
                        <s className={styles.bundleComparePrice}>{formatProductMoney(pricing.compare_at_total, currencyCode)}</s>
                      ) : null}
                    </>
                  ) : <span className={styles.unavailable}>Kullanılamıyor</span>}
                </div>
              </div>

              {popularText ? <div className={styles.mostPopularBadge}>{popularText}</div> : null}

              {hasSelectors && isSelected ? (
                <div
                  data-testid={`bundle-variant-selectors-${option.id}`}
                  data-state="open"
                  className={`${styles.variantSelectors} ${styles.variantSelectorsOpen}`}
                >
                  {selectorNote ? <p className={styles.selectorNote}>{selectorNote}</p> : null}
                  {slots.map((slot, slotIndex) => {
                    const productChoice = catalog.find((candidate) => candidate.product_id === slot.product_id)
                    const productKey = `${bundle.id}:${option.id}:${slotIndex}:product`
                    const variantKey = `${bundle.id}:${option.id}:${slotIndex}:variant`
                    const variants = productChoice?.variants ?? []
                    const selectedVariant = variants.find((candidate) => candidate.variant_id === slot.variant_id)
                    return (
                      <div className={styles.variantSlot} key={`${slotIndex}-${slot.product_id}-${slot.variant_id}`}>
                        <div className={styles.variantSlotHeader}>{slotIndex + 1}. {slotLabel} · Ürünü seç / değiştir</div>
                        <div className={styles.variantSlotOptions}>
                          <div className={styles.popoverWrap}>
                            <button
                              type="button"
                              tabIndex={isSelected ? 0 : -1}
                              className={styles.variantSelect}
                              aria-label={`Paket ürünü ${slotIndex + 1}`}
                              aria-invalid={!slot.product_id ? true : undefined}
                              aria-haspopup="listbox"
                              aria-expanded={menu?.key === productKey}
                              onClick={(event) => toggleMenu(productKey, event.currentTarget)}
                            >
                              <strong className={styles.variantTriggerValue}>
                                {productChoice
                                  ? bundleProductLabel(productChoice.title)
                                  : PRODUCT_COPY.bundleSelectProduct}
                              </strong>
                              <SelectorChevron
                                testId={`bundle-product-chevron-${slotIndex + 1}`}
                                attention={isSelected && !hasOpenedSelector ? "active" : "stopped"}
                                className={`${styles.selectChevron} ${isSelected && !hasOpenedSelector ? styles.selectChevronAttention : ""} ${menu?.key === productKey ? styles.selectChevronOpen : ""}`}
                              />
                            </button>
                            {menu?.key === productKey ? (
                              <div
                                ref={menuRef}
                                role="listbox"
                                aria-label={`Paket ürünü ${slotIndex + 1} seçenekleri`}
                                className={styles.menu}
                              >
                                {catalog.map((candidate) => {
                                  const availableVariants = candidate.variants.filter((variant) => variant.available)
                                  return (
                                    <button
                                      key={candidate.product_id}
                                      type="button"
                                      role="option"
                                      aria-selected={candidate.product_id === slot.product_id}
                                      disabled={!availableVariants.length}
                                      className={styles.menuItem}
                                      onClick={() => {
                                        changeSlot(bundle, option, slotIndex, {
                                          product_id: candidate.product_id,
                                          variant_id: availableVariants.length === 1 ? availableVariants[0].variant_id : "",
                                        })
                                        setMenu(null)
                                      }}
                                    >{bundleProductLabel(candidate.title)}</button>
                                  )
                                })}
                              </div>
                            ) : null}
                          </div>
                          {productChoice && variants.length > 1 ? (
                            <div className={styles.popoverWrap}>
                              <button
                                type="button"
                                tabIndex={isSelected ? 0 : -1}
                                className={styles.variantSelect}
                                aria-label={`Paket varyantı ${slotIndex + 1}`}
                                aria-invalid={!slot.variant_id ? true : undefined}
                                aria-haspopup="listbox"
                                aria-expanded={menu?.key === variantKey}
                                onClick={(event) => toggleMenu(variantKey, event.currentTarget)}
                              >
                                <strong>{selectedVariant?.title ?? "Varyant seç"}</strong>
                                <SelectorChevron
                                  className={`${styles.selectChevron} ${menu?.key === variantKey ? styles.selectChevronOpen : ""}`}
                                />
                              </button>
                              {menu?.key === variantKey ? (
                                <div
                                  ref={menuRef}
                                  role="listbox"
                                  aria-label={`Paket varyantı ${slotIndex + 1} seçenekleri`}
                                  className={styles.menu}
                                >
                                  {variants.map((variant) => (
                                    <button
                                      key={variant.variant_id}
                                      type="button"
                                      role="option"
                                      aria-selected={variant.variant_id === slot.variant_id}
                                      disabled={!variant.available}
                                      className={styles.menuItem}
                                      onClick={() => {
                                        changeSlot(bundle, option, slotIndex, { variant_id: variant.variant_id })
                                        setMenu(null)
                                      }}
                                    >{variant.title}</button>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </div>
          )
        }))}
      </div>

      {companionProducts.length ? (
        <div className={styles.bundleCompanionProducts}>
          {companionProducts.map((candidate) => {
            const shortName = shortProductName(candidate.title)
            return (
              <LocalizedClientLink
                key={candidate.product_id}
                className={styles.bundleCompanionButton}
                href={`/products/${candidate.handle}`}
                aria-label={`${shortName}'i ${PRODUCT_COPY.inspect}`}
              >
                <span className={styles.bundleCompanionContent}>
                  <span className={styles.bundleCompanionIcon} aria-hidden="true">{companionIcon(shortName)}</span>
                  <span className={styles.bundleCompanionProductName}>{shortName}'i</span>
                  <span className={styles.bundleCompanionAction}>{PRODUCT_COPY.inspect}</span>
                </span>
              </LocalizedClientLink>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}
